import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import i18n from 'i18n-js';
import { Table } from 'components/UI';
import Box from '@material-ui/core/Box';
import { BaseModal } from 'components/UI';
import Grid from '@material-ui/core/Grid';
import { Formik, Form, Field } from 'formik';
import Button from '@material-ui/core/Button';
import { IconButton } from '@material-ui/core';
import Divider from '@material-ui/core/Divider';
import DeleteIcon from '@material-ui/icons/Delete';
import { usersActions } from 'state/modules/users';
import Typography from '@material-ui/core/Typography';
import { useDispatch, useSelector } from 'react-redux';
import { VehiclesGroupsSelect } from 'components/AsyncFields';
import { VehiclesGroupsActions } from 'state/modules/vehiclesGroups';

const AssignVehicleGroupsModal = ({ id, open, handleClick }) => {
  const dispatch = useDispatch();
  const formRef = useRef(null);
  const params = { limit: 5, user_id: id, access_type: 'DIRECT' };

  const AssignVehicleGroupsSchema = Yup.object().shape({
    vehicle_group_id: Yup.string().required(i18n.t('common.required_field')),
  });

  const { vehicleGroups, loading, pagination } = useSelector((state) => ({
    vehicleGroups: state.vehiclesGroups.modal,
    loading: state.vehiclesGroups.modalLoading,
    pagination: state.vehiclesGroups.modalPagination,
  }));

  const { fetchedAddUserVehicleGroupSuccess, fetchedDeleteUserVehicleGroupSuccess } = useSelector(
    (state) => state.users
  );

  // Fetch VG on load
  useEffect(() => {
    dispatch(VehiclesGroupsActions.fetchVehicleGroups(params, 'modal'));
  }, []);

  // Fetch VG on update
  useEffect(() => {
    if (fetchedDeleteUserVehicleGroupSuccess || fetchedAddUserVehicleGroupSuccess) {
      dispatch(VehiclesGroupsActions.fetchVehicleGroups(params, 'modal'));
    }
  }, [id, fetchedAddUserVehicleGroupSuccess, fetchedDeleteUserVehicleGroupSuccess]);

  const onSubmit = (values) => {
    const payload = { ...{ group_id: values.vehicle_group_id } };
    dispatch(usersActions.fetchAddUserVehicleGroups(id, payload));
  };

  const columns = [
    {
      name: 'ID',
      selector: 'id',
      grow: 1,
    },
    {
      name: 'Name',
      selector: 'name',
      cell: (row) => (
        <>
          {row.group_type === 'OWNER'
            ? i18n.t('user_groups.owner_vehicle_group')
            : i18n.t('user_groups.vehicle_group')}{' '}
          - {row.name}
        </>
      ),
      grow: 4,
    },
    {
      name: 'Actions',
      selector: '',
      grow: 1,
      cell: (row) => (
        <IconButton onClick={() => handleDeleteItem(row)}>
          {' '}
          <DeleteIcon />{' '}
        </IconButton>
      ),
    },
  ];

  const handleDeleteItem = (item) => {
    const groupId = item.id;
    dispatch(usersActions.fetchDeleteUserVehicleGroups(id, groupId));
  };

  const handlePageChange = (page) => {
    dispatch(VehiclesGroupsActions.fetchVehicleGroups({ ...params, page }, 'modal'));
  };

  const handleClose = () => {
    usersActions.resetAddVehicleGroups();
    handleClick();
  };

  return (
    <BaseModal
      size="md"
      hasHeader
      open={open}
      hasFooter={false}
      hasCancelButton={true}
      closeButtonType="icon"
      handleClick={handleClose}
      title={i18n.t('vehicle_groups.user')}
    >
      <Grid container direction="column" alignItems="flex-start">
        <Typography variant="body2" gutterBottom>
          <Box mt={1} mb={3} fontSize={13} component="span">
            {i18n.t('vehicle_groups.groups_assigned_to_customers')}:
          </Box>
        </Typography>
        <Box width="100%">
          <Table
            columns={columns}
            loading={loading}
            data={vehicleGroups}
            paginationPerPage={5}
            onPageChange={handlePageChange}
            paginationRowsPerPageOptions={[5]}
            totalRows={pagination?.totalRows || 0}
          />
          <Formik
            innerRef={formRef}
            onSubmit={onSubmit}
            initialValues={{ vehicle_group_id: '' }}
            validationSchema={AssignVehicleGroupsSchema}
          >
            {({ handleSubmit }) => (
              <Form noValidate onSubmit={handleSubmit}>
                <Box mt={1}>
                  <Field
                    name="vehicle_group_id"
                    label={i18n.t('vehicle_groups.vehicle_groups')}
                    omitFields={[...vehicleGroups.map((i) => i.name)]}
                    omitFieldKey={'name'}
                    customParams={{
                      group_type: ['CUSTOMER', 'OWNER'],
                      limit: 10,
                    }}
                    component={VehiclesGroupsSelect}
                  />
                  <Box my={3}>
                    <Divider />
                  </Box>
                </Box>
                <Box mt={5} display="flex" justifyContent="flex-end">
                  <Button
                    fullWidth
                    type="button"
                    variant="text"
                    onClick={() => {
                      usersActions.resetAddVehicleGroups();
                      handleClick();
                    }}
                    style={{ maxWidth: 200 }}
                    disabled={loading}
                  >
                    {i18n.t('common.cancel')}
                  </Button>
                  <Button
                    fullWidth
                    type="submit"
                    variant="text"
                    color="primary"
                    style={{ maxWidth: 200 }}
                    disabled={loading}
                  >
                    {i18n.t('vehicle_groups.add_service_group')}
                  </Button>
                </Box>
              </Form>
            )}
          </Formik>
        </Box>
      </Grid>
    </BaseModal>
  );
};

AssignVehicleGroupsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClick: PropTypes.func.isRequired,
};

AssignVehicleGroupsModal.defaultProps = {
  open: false,
  handleClick: () => {},
};

export default AssignVehicleGroupsModal;
